// src/components/paymentUtils.js

import * as XLSX from "xlsx";

/**
 * Gets a date approximately 3 months ago from today.
 * @returns {Date} A Date object representing 3 months ago.
 */
export const getThreeMonthsAgo = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 3);
  return date;
};

/**
 * Validates if the date range between startDate and endDate is within 90 days (approx 3 months).
 * @param {Date | null} startDate
 * @param {Date | null} endDate
 * @returns {boolean} True if the date range is valid or if either date is null, false otherwise.
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return true; // Allow validation if one date is missing (handled by Calendar min/max)

  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const maxDays = 90; // Approximately 3 months

  return diffDays <= maxDays;
};

/**
 * Formats a Date object into a YYYY-MM-DD string for API consumption.
 * @param {Date | null} date
 * @returns {string | null} Formatted date string or null.
 */
export const formatDateForAPI = (date) => {
  if (!date) return null;
  return date.toISOString().split("T")[0]; // YYYY-MM-DD format
};

/**
 * Copies text to the clipboard and shows a toast notification.
 * @param {string} text - The text to copy.
 * @param {React.MutableRefObject} toastRef - Ref to the PrimeReact Toast component.
 */
export const copyToClipboard = async (text, toastRef) => {
  try {
    await navigator.clipboard.writeText(text);
    toastRef.current?.show({
      severity: "success",
      summary: "Copied",
      detail: "Order ID copied to clipboard",
      life: 2000,
    });
  } catch (err) {
    console.error("Failed to copy text: ", err);
    toastRef.current?.show({
      severity: "error",
      summary: "Error",
      detail: "Could not copy text",
      life: 2000,
    });
  }
};

/**
 * Formats a date-time string or Date object to a locale-specific string.
 * @param {string | Date | null} dateTime
 * @returns {string} Formatted date-time string or "N/A".
 */
export const toLocale = (dateTime) => {
  if (!dateTime) return "N/A";
  const dateObj = new Date(dateTime);
  if (isNaN(dateObj.getTime())) return "Invalid Date"; // Handle invalid date strings
  return dateObj.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Formats an amount to a currency string (INR by default).
 * @param {number | string | null | undefined} amount
 * @param {string} currency
 * @returns {string} Formatted currency string or "N/A".
 */
export const formatAmount = (amount, currency = "INR") => {
  if (amount === null || amount === undefined) return "N/A";
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(numericAmount);
};

/**
 * Gets the severity for a given payment status for PrimeReact Tag component.
 * @param {string} status
 * @returns {string} Severity string.
 */
export const getStatusSeverity = (status) =>
  ({
    SUCCESS: "success",
    FAILED: "danger",
    PENDING: "warning",
    PROCESSING: "info",
  }[status] || "secondary");

/**
 * Extracts UPI ID from payment method object.
 * @param {object | null} paymentMethod
 * @returns {string} UPI ID or "N/A".
 */
export const getUpiId = (paymentMethod) => {
  return paymentMethod?.upi?.upi_id || "N/A";
};

/**
 * Determines payment method type from payment method object.
 * @param {object | null} paymentMethod
 * @returns {string} Payment method type (e.g., "UPI") or "N/A".
 */
export const getPaymentMethodType = (paymentMethod) => {
  return paymentMethod?.upi ? "UPI" : "N/A";
};

/**
 * Exports payment data to an Excel file.
 * @param {Array<object>} data - The payment records to export.
 * @param {object} filters - The currently applied filters, used for filename.
 * @param {React.MutableRefObject} toastRef - Ref to the PrimeReact Toast component.
 */
export const exportToExcel = (data, filters, toastRef) => {
  try {
    if (!data || data.length === 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "No Data",
        detail: "No data available to export",
        life: 3000,
      });
      return;
    }

    const exportData = data.map((item) => ({
      "Order ID": item.order_id || "N/A",
      Amount: item.payment_amount
        ? `₹${parseFloat(item.payment_amount).toFixed(2)}`
        : "N/A",
      Status: item.payment_status || "N/A",
      "Order Date": toLocale(item.order_created_at),
      "Payment Date": toLocale(item.payment_completion_time),
      "Customer Number": item.customer_number || "N/A",
      "Customer ID": item.customer_id || "N/A",
      "Loan Number": item.loan_number || "N/A",
      "Payment Method": getPaymentMethodType(item.payment_method),
      "UPI ID": getUpiId(item.payment_method),
      "Bank Reference": item.bank_reference || "N/A",
      "CF Payment ID": item.cf_payment_id || "N/A",
      "Payment Message": item.payment_message || "N/A",
      "Receipt Number": item.reciept_no || item.payment_receipt || "N/A",
      "Created At": toLocale(item.created_at),
      "Modified Date": toLocale(item.modified_date),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");

    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    let filename = `payments_export_${dateStr}`;

    if (filters.start_date && filters.end_date) {
      const startStr = formatDateForAPI(filters.start_date);
      const endStr = formatDateForAPI(filters.end_date);
      filename += `_${startStr}_to_${endStr}`;
    }

    filename += ".xlsx";

    XLSX.writeFile(wb, filename);

    toastRef.current?.show({
      severity: "success",
      summary: "Export Successful",
      detail: `Data exported to ${filename}`,
      life: 4000,
    });
  } catch (error) {
    console.error("Export failed:", error);
    toastRef.current?.show({
      severity: "error",
      summary: "Export Failed",
      detail: "Failed to export data to Excel",
      life: 3000,
    });
  }
};